import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { FloppyDisk, ArrowCounterClockwise, Eye, EyeSlash, ShieldCheck, Copy, Link as LinkIcon, Phone, Plus, Trash, Lightning, Users as UsersIcon, Calendar, X, EnvelopeSimple, Paperclip, PaperPlaneTilt, WhatsappLogo, Funnel, ArrowsSplit, Sparkle } from "@phosphor-icons/react";
import { fmtIST } from "@/lib/format";
import { useCompany } from "@/context/CompanyContext";
import { CallAiSettingsPanel } from "@/components/CallAiSummary";

const FIELDS = [
  { k: "access_token",          label: "Access Token",              secret: true,  hint: "Permanent System User token with whatsapp_business_messaging + whatsapp_business_management scopes." },
  { k: "phone_number_id",       label: "Phone Number ID",           hint: "Meta → WhatsApp Manager → Phone numbers. Numeric id." },
  { k: "waba_id",               label: "WhatsApp Business Account ID", hint: "The parent Business Account id (needed to list templates)." },
  { k: "api_version",           label: "API Version",               hint: "e.g. v22.0 — update when Meta releases a newer stable version." },
  { k: "verify_token",          label: "Webhook Verify Token",      hint: "Paste this exact string into Meta → WhatsApp → Configuration → Webhooks → Verify token." },
  { k: "app_secret",            label: "App Secret (optional)",     secret: true,  hint: "Meta App → Settings → Basic → App Secret. Used to verify webhook signatures." },
  { k: "default_template",      label: "Default Welcome Template",  hint: "Template name used for the first-touch message when a new lead is created. Must be APPROVED in Meta." },
  { k: "default_template_lang", label: "Default Template Language", hint: "Language code registered on the template, e.g. en_US, en, hi." },
];

// Settings are grouped into sections; the section menu shows one at a time
// (?section=... keeps it in the URL so a link opens the right one).
const SECTIONS = [
  { k: "whatsapp", label: "WhatsApp", icon: WhatsappLogo, desc: "Meta Cloud API connection, welcome template and the auto-reply sequence." },
  { k: "sources", label: "Lead sources", icon: Funnel, desc: "Where leads come from: webhook URLs (IndiaMART, JustDial, Shopify, WhatsApp, Gmail), ExportersIndia and TradeIndia." },
  { k: "routing", label: "Lead routing", icon: ArrowsSplit, desc: "Who gets which leads: buy-lead routing per source and IndiaMART call (PNS) receiver numbers." },
  { k: "calls", label: "Calls & AI", icon: Sparkle, desc: "AI call summaries, automatic follow-ups, team calls and recording settings." },
  { k: "email", label: "Email", icon: EnvelopeSimple, desc: "Automatic welcome email to new leads." },
  { k: "team", label: "Team", icon: Calendar, desc: "Leave and holiday management." },
];

// Every panel below reads/writes config for the ACTIVE company (X-Company header
// is attached by lib/api.js). This banner makes that unmistakable so the admin
// never pastes Fragvansh credentials into the CitSpray config or vice-versa.
function CompanyScopeBanner() {
  const { companyLabel, isFragvansh } = useCompany();
  return (
    <div className={`border px-4 py-3 flex items-center gap-2 ${isFragvansh ? "border-[#7C3AED] bg-[#F5F3FF]" : "border-[#002FA7] bg-[#EAF1FF]"}`}
      data-testid="settings-company-banner">
      <ShieldCheck size={16} className={isFragvansh ? "text-[#7C3AED]" : "text-[#002FA7]"} />
      <span className="text-sm">
        Configuring: <b>{companyLabel}</b>
        <span className="text-xs text-gray-500 ml-2">WhatsApp, ExportersIndia and webhook URLs below apply to this company only — switch companies in the sidebar to configure the other.</span>
      </span>
    </div>
  );
}

export default function Settings() {
  const [params, setParams] = useSearchParams();
  const section = SECTIONS.some((s) => s.k === params.get("section")) ? params.get("section") : "whatsapp";
  const setSection = (k) => setParams({ section: k }, { replace: true });
  const [cfg, setCfg] = useState(null);
  const [hooks, setHooks] = useState(null);
  const [form, setForm] = useState({});
  const [reveal, setReveal] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [{ data: cfgD }, { data: hooksD }] = await Promise.all([
        api.get("/settings/whatsapp"),
        api.get("/settings/webhooks-info"),
      ]);
      setCfg(cfgD); resetForm(cfgD);
      setHooks(hooksD);
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const resetForm = (data) => {
    const f = {};
    for (const { k, secret } of FIELDS) {
      if (secret) {
        f[k] = ""; // don't pre-fill secrets — leave blank means "keep current"
      } else {
        f[k] = data?.effective?.[k] ?? "";
      }
    }
    setForm(f);
  };

  const onChange = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    // Only send fields that differ from current or are non-blank for secrets
    const patch = {};
    for (const { k, secret } of FIELDS) {
      const v = form[k];
      if (secret) {
        // For secrets: empty input => don't change. Use a separate CLEAR button per-field.
        if (v && v.trim()) patch[k] = v.trim();
      } else {
        const current = cfg?.effective?.[k] ?? "";
        if ((v ?? "") !== current) patch[k] = v;
      }
    }
    if (Object.keys(patch).length === 0) {
      toast.info("Nothing to update");
      setSaving(false);
      return;
    }
    try {
      await api.put("/settings/whatsapp", patch);
      toast.success("Saved — new config is live");
      await load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const clearField = async (k) => {
    if (!window.confirm(`Clear the saved override for "${k}" and fall back to the .env default?`)) return;
    try {
      await api.put("/settings/whatsapp", { [k]: "" });
      toast.success(`${k} override cleared`);
      await load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!cfg) return <div className="p-8 text-xs uppercase tracking-widest text-gray-500">Loading…</div>;

  const hasOverride = (k) => Object.prototype.hasOwnProperty.call(cfg.overrides || {}, k)
    || Object.prototype.hasOwnProperty.call(cfg.overrides || {}, `${k}_masked`);

  const sec = SECTIONS.find((s) => s.k === section);
  return (
    <div className="p-4 md:p-8 space-y-4">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Admin</div>
        <h1 className="font-chivo font-black text-2xl md:text-4xl">Settings</h1>
      </div>

      <CompanyScopeBanner />

      <div className="flex flex-col md:flex-row gap-4 md:gap-6 items-start">
        {/* Section menu: a column on desktop, a scrollable tab row on phones */}
        <nav className="w-full md:w-56 shrink-0 md:sticky md:top-4 flex md:flex-col gap-1 overflow-x-auto border-b md:border-b-0 border-gray-200 pb-1 md:pb-0"
          data-testid="settings-sections">
          {SECTIONS.map(({ k, label, icon: Icon }) => (
            <button key={k} onClick={() => setSection(k)} data-testid={`settings-section-${k}`}
              className={`flex items-center gap-2 px-3 py-2.5 text-sm text-left whitespace-nowrap border-l-0 md:border-l-2 border-b-2 md:border-b-0 transition-colors ${
                section === k ? "md:border-l-[#002FA7] border-b-[#002FA7] md:border-b-transparent bg-white font-semibold text-gray-900"
                              : "border-transparent text-gray-600 hover:bg-gray-100"}`}>
              <Icon size={16} /> {label}
            </button>
          ))}
        </nav>

        <div className="flex-1 min-w-0 max-w-4xl space-y-6 w-full">
          <div className="border-b border-gray-200 pb-3">
            <h2 className="font-chivo font-black text-xl">{sec.label}</h2>
            <p className="text-xs text-gray-500 mt-1">{sec.desc}</p>
          </div>

          {section === "sources" && (
            <>
              {hooks && <WebhooksPanel hooks={hooks} />}
              <ExportersIndiaPanel onChanged={load} />
              <TradeIndiaPanel />
            </>
          )}
          {section === "routing" && (
            <>
              <BuyleadsRoutingPanel />
              <CallRoutingPanel />
              <PnsNumbersPanel />
            </>
          )}
          {section === "calls" && <CallAiSettingsPanel />}
          {section === "email" && <EmailAutoSendPanel />}
          {section === "team" && <LeaveManagementPanel />}
          {section === "whatsapp" && (
            <>
      <form onSubmit={submit} className="border border-gray-200 bg-white" data-testid="whatsapp-settings-form">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="font-chivo font-bold text-lg">WhatsApp Cloud API</h2>
            <p className="text-xs text-gray-500 mt-1">
              Values live-override the defaults from <span className="kbd">backend/.env</span>.
              Leave secret fields blank to keep the current value; use <b>Clear</b> to drop an override.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {cfg.effective.enabled
              ? <span className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest font-bold bg-[#008A00] text-white"><ShieldCheck size={12} /> Active</span>
              : <span className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest font-bold bg-[#E60000] text-white">Disabled</span>}
          </div>
        </div>

        <div className="divide-y divide-gray-200">
          {FIELDS.map(({ k, label, secret, hint }) => {
            const overridden = hasOverride(k);
            const currentMasked = secret
              ? (cfg.effective?.[`${k}_masked`] || "(not set)")
              : (cfg.effective?.[k] ?? "(not set)");
            return (
              <div key={k} className="px-5 py-4 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid={`wa-field-${k}`}>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold flex items-center gap-2">
                    {label}
                    {overridden && <span className="bg-[#002FA7] text-white px-1.5 py-0.5 text-[9px] font-bold">OVERRIDE</span>}
                  </div>
                  <div className="text-xs text-gray-500 mt-1 leading-relaxed">{hint}</div>
                  <div className="text-[10px] uppercase tracking-widest text-gray-400 font-bold mt-2">Current</div>
                  <div className="font-mono text-xs break-all" data-testid={`wa-current-${k}`}>{String(currentMasked) || "—"}</div>
                </div>
                <div className="md:col-span-2">
                  <div className="flex gap-2 items-center">
                    <input
                      type={secret && !reveal[k] ? "password" : "text"}
                      value={form[k] ?? ""}
                      onChange={(e) => onChange(k, e.target.value)}
                      placeholder={secret ? "Leave blank to keep current" : "Enter value"}
                      className="flex-1 border border-gray-300 px-3 py-2 text-sm font-mono focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7] outline-none"
                      data-testid={`wa-input-${k}`}
                    />
                    {secret && (
                      <button type="button" onClick={() => setReveal((r) => ({ ...r, [k]: !r[k] }))} className="text-gray-500 hover:text-gray-900 p-2 border border-gray-300" title="Toggle reveal">
                        {reveal[k] ? <EyeSlash size={14} /> : <Eye size={14} />}
                      </button>
                    )}
                    {overridden && (
                      <button type="button" onClick={() => clearField(k)} className="text-[10px] uppercase tracking-widest font-bold text-[#E60000] border border-[#E60000] hover:bg-[#E60000] hover:text-white px-2 py-2" data-testid={`wa-clear-${k}`}>
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
          <div className="text-[10px] uppercase tracking-widest text-gray-400 font-bold">
            Webhook URL (paste in Meta)
            <div className="font-mono text-xs text-gray-700 mt-1 normal-case tracking-normal break-all">
              {window.location.origin}/api/webhooks/whatsapp
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => resetForm(cfg)} className="border border-gray-300 px-4 py-2 text-[10px] uppercase tracking-widest font-bold hover:bg-gray-100 flex items-center gap-1" data-testid="wa-reset-btn">
              <ArrowCounterClockwise size={12} /> Reset
            </button>
            <button type="submit" disabled={saving} className="bg-[#002FA7] hover:bg-[#002288] text-white px-4 py-2 text-[10px] uppercase tracking-widest font-bold flex items-center gap-1 disabled:opacity-50" data-testid="wa-save-btn">
              <FloppyDisk size={12} weight="bold" /> {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </div>
      </form>
              <WhatsAppAutoSequencePanel />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function WebhooksPanel({ hooks }) {
  const items = [
    { ...hooks.whatsapp,             key: "whatsapp" },
    { ...hooks.indiamart,            key: "indiamart" },
    { ...hooks.exportersindia,       key: "exportersindia" },
    { ...hooks.gmaps,                key: "gmaps" },
    { ...hooks.gmail,                key: "gmail" },
    { ...hooks.justdial_manual_ingest, key: "justdial" },
  ].filter((x) => x && (x.url || x.label));
  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copied`); }
    catch { toast.error("Copy failed — please copy manually"); }
  };
  return (
    <div className="border border-gray-200 bg-white" data-testid="webhooks-panel">
      <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h2 className="font-chivo font-bold text-lg flex items-center gap-2"><LinkIcon size={18} weight="bold" /> Webhook URLs</h2>
          <p className="text-xs text-gray-500 mt-1">Paste these into the respective platform dashboards.</p>
        </div>
      </div>
      <div className="divide-y divide-gray-200">
        {items.map(it => (
          <div key={it.key} className="px-5 py-4" data-testid={`hook-${it.key}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="font-chivo font-bold">{it.label}</div>
                <div className="text-xs text-gray-500 mt-1">{it.where_to_paste}</div>
                <div className="mt-2 flex items-center gap-2">
                  <span className="kbd">{it.method}</span>
                  {it.subscribe_fields && it.subscribe_fields.length > 0 && (
                    <span className="text-[10px] uppercase tracking-widest text-gray-400 font-bold">Subscribe: {it.subscribe_fields.join(", ")}</span>
                  )}
                  {it.auth && <span className="text-[10px] uppercase tracking-widest text-gray-400 font-bold">Auth: {it.auth}</span>}
                </div>
              </div>
            </div>
            <div className="mt-3 flex gap-2 items-stretch">
              <code className="flex-1 font-mono text-xs bg-gray-50 border border-gray-200 px-3 py-2 break-all" data-testid={`hook-url-${it.key}`}>
                {it.url}
              </code>
              <button onClick={() => copy(it.url, "URL")} className="border border-gray-900 px-3 py-2 text-[10px] uppercase tracking-widest font-bold hover:bg-gray-900 hover:text-white flex items-center gap-1" data-testid={`copy-url-${it.key}`}>
                <Copy size={12} /> Copy
              </button>
            </div>
            {it.verify_token && (
              <div className="mt-2 flex gap-2 items-stretch">
                <div className="flex-1 flex">
                  <span className="bg-gray-100 border border-gray-200 border-r-0 px-2 py-2 text-[10px] uppercase tracking-widest text-gray-500 font-bold">Verify token</span>
                  <code className="flex-1 font-mono text-xs bg-gray-50 border border-gray-200 px-3 py-2 break-all" data-testid={`hook-verify-${it.key}`}>{it.verify_token}</code>
                </div>
                <button onClick={() => copy(it.verify_token, "Verify token")} className="border border-gray-900 px-3 py-2 text-[10px] uppercase tracking-widest font-bold hover:bg-gray-900 hover:text-white flex items-center gap-1">
                  <Copy size={12} /> Copy
                </button>
              </div>
            )}
            {it.ingest_key && (
              <div className="mt-2 flex gap-2 items-stretch">
                <div className="flex-1 flex">
                  <span className="bg-gray-100 border border-gray-200 border-r-0 px-2 py-2 text-[10px] uppercase tracking-widest text-gray-500 font-bold">Ingest key</span>
                  <code className="flex-1 font-mono text-xs bg-gray-50 border border-gray-200 px-3 py-2 break-all" data-testid={`hook-ingest-key-${it.key}`}>{it.ingest_key}</code>
                </div>
                <button onClick={() => copy(it.ingest_key, "Ingest key")} className="border border-gray-900 px-3 py-2 text-[10px] uppercase tracking-widest font-bold hover:bg-gray-900 hover:text-white flex items-center gap-1">
                  <Copy size={12} /> Copy
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


function PnsNumbersPanel() {
  const [d, setD] = useState(null);
  const [txt, setTxt] = useState("");
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try { const { data } = await api.get("/settings/indiamart-pns"); setD(data); setTxt((data.numbers || []).join("\n")); }
    catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings/indiamart-pns", { numbers: txt.split(/[\n,]+/).map((x) => x.trim()).filter(Boolean) });
      setD(data); toast.success("Saved");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  if (!d) return null;
  const s = d.stats || {};
  return (
    <div className="border border-gray-200 bg-white" data-testid="pns-numbers-panel">
      <div className="px-5 py-4 border-b border-gray-200">
        <h2 className="font-chivo font-bold text-lg flex items-center gap-2"><Phone size={18} weight="bold" /> IndiaMART call (PNS) numbers</h2>
        <p className="text-xs text-gray-500 mt-1 leading-relaxed">
          Buyers calling through IndiaMART show up on your phones as IndiaMART's number, not their own. Calls from these
          numbers are matched to the right buyer using IndiaMART's call details (call time and length); a call-back made
          through the same number is matched to the buyer who called in the hour before (marked “probable”).
        </p>
      </div>
      <div className="p-5 grid md:grid-cols-2 gap-5 text-sm">
        <div className="space-y-2">
          <textarea value={txt} onChange={(e) => setTxt(e.target.value)} rows={3} className="w-full border border-gray-300 px-2 py-1.5 text-xs font-mono"
            placeholder="One number per line" data-testid="pns-numbers-input" />
          <button disabled={busy} onClick={save} className="bg-[#002FA7] text-white px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold disabled:opacity-50">Save numbers</button>
        </div>
        <div className="text-xs space-y-1">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Calls on these numbers</div>
          <div><b>{s.exact || 0}</b> matched to the buyer</div>
          <div><b>{s.callback || 0}</b> call-backs matched (probable)</div>
          <div><b>{s.none || 0}</b> could not be matched</div>
          <div><b>{s.waiting || 0}</b> waiting for IndiaMART's details</div>
        </div>
      </div>
    </div>
  );
}

function CallRoutingPanel() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [drafts, setDrafts] = useState({}); // { user_id: "new number input" }

  const load = async () => {
    try {
      const { data } = await api.get("/settings/receiver-routing");
      setRows(data.users || []);
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const saveNumbers = async (userId, numbers) => {
    setBusy(true);
    try {
      await api.put(`/users/${userId}/receiver-numbers`, { receiver_numbers: numbers });
      toast.success("Receiver numbers updated");
      setDrafts((d) => ({ ...d, [userId]: "" }));
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const addNumber = (row) => {
    const draft = (drafts[row.id] || "").trim();
    if (!draft) return;
    const next = [...(row.receiver_numbers || []), draft];
    saveNumbers(row.id, next);
  };

  const removeNumber = (row, n) => {
    const next = (row.receiver_numbers || []).filter(x => x !== n);
    saveNumbers(row.id, next);
  };

  return (
    <div className="border border-gray-200 bg-white" data-testid="call-routing-panel">
      <div className="px-5 py-4 border-b border-gray-200">
        <h2 className="font-chivo font-bold text-lg flex items-center gap-2"><Phone size={18} weight="bold" /> Call Routing / Receiver Numbers</h2>
        <p className="text-xs text-gray-500 mt-1 leading-relaxed">
          Map each user to one or more <b>IndiaMART receiver numbers</b>. When a PNS / call-tracked
          enquiry arrives, the lead is auto-assigned to the user whose receiver number matched.
          A number can only belong to one user.
        </p>
      </div>
      <div className="divide-y divide-gray-200">
        {rows.map(row => (
          <div key={row.id} className="px-5 py-4" data-testid={`routing-row-${row.username}`}>
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div>
                <div className="font-semibold text-sm">{row.name} <span className="text-gray-400 text-xs font-mono">@{row.username}</span></div>
                <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">{row.role}</div>
              </div>
              <div className="text-[10px] uppercase tracking-widest text-gray-400 font-bold">
                {(row.receiver_numbers || []).length} number{(row.receiver_numbers || []).length === 1 ? "" : "s"}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mb-2">
              {(row.receiver_numbers || []).length === 0 && (
                <span className="text-xs text-gray-400 italic">No numbers mapped — calls / PNS leads on these numbers will go through round-robin instead.</span>
              )}
              {(row.receiver_numbers || []).map(n => (
                <span key={n} className="inline-flex items-center gap-1 bg-gray-100 border border-gray-300 px-2 py-1 text-xs font-mono" data-testid={`receiver-${row.username}-${n}`}>
                  {n}
                  <button onClick={() => removeNumber(row, n)} disabled={busy} className="text-[#E60000] hover:bg-[#E60000] hover:text-white p-0.5" title="Remove">
                    <Trash size={11} />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={drafts[row.id] || ""}
                onChange={(e) => setDrafts(d => ({ ...d, [row.id]: e.target.value }))}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addNumber(row); } }}
                placeholder="+91 9876543210"
                className="flex-1 border border-gray-300 px-3 py-1.5 text-sm font-mono outline-none focus:border-[#002FA7]"
                data-testid={`add-receiver-input-${row.username}`}
              />
              <button
                onClick={() => addNumber(row)}
                disabled={busy || !(drafts[row.id] || "").trim()}
                className="bg-[#002FA7] hover:bg-[#002288] text-white px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold flex items-center gap-1 disabled:opacity-50"
                data-testid={`add-receiver-btn-${row.username}`}
              >
                <Plus size={12} weight="bold" /> Add
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ExportersIndiaPanel({ onChanged }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [email, setEmail] = useState("");
  const [mins, setMins] = useState(1);
  const [secs, setSecs] = useState(0);
  const [reveal, setReveal] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/settings/exportersindia-pull");
      setCfg(data);
      setEmail(data.email || "");
      setMins(data.interval_minutes ?? 1);
      setSecs(data.interval_seconds ?? 0);
      setApiKey("");
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      await api.put("/settings/exportersindia-pull", patch);
      toast.success("Saved");
      await load();
      onChanged?.();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/settings/exportersindia-pull/run-now");
      const n = (data.created || []).length;
      toast.success(n ? `Pulled ${n} new lead(s)` : `Pull OK — ${data.skipped_empty ? "no new enquiries" : "nothing to do"}`);
      await load();
      onChanged?.();
    } catch (e) { toast.error(errMsg(e, "Pull failed")); }
    finally { setRunning(false); }
  };

  const toggleEnabled = async () => {
    const next = !cfg.enabled;
    if (next && (!apiKey.trim() && !cfg.has_key)) { toast.error("Set an API key first"); return; }
    if (next && !email.trim()) { toast.error("Set an email first"); return; }
    await save({ enabled: next });
  };

  if (!cfg) return null;
  const lastSuccess = cfg.last_success_at ? fmtIST(cfg.last_success_at) : "Never";
  return (
    <div className="border border-gray-200 bg-white" data-testid="exportersindia-panel">
      <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between gap-3">
        <div>
          <h2 className="font-chivo font-bold text-lg flex items-center gap-2"><ShieldCheck size={18} weight="bold" /> ExportersIndia Pull API</h2>
          <p className="text-xs text-gray-500 mt-1 max-w-2xl">
            LeadOrbit polls ExportersIndia on an interval you choose and imports new enquiries
            automatically — no webhook needed on their side. You can rotate the API key any time.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="ei-pull-enable-toggle">
          <input type="checkbox" checked={cfg.enabled} onChange={toggleEnabled} className="w-4 h-4 accent-[#25D366]" />
          <span className="text-[10px] uppercase tracking-widest font-bold">{cfg.enabled ? "Enabled" : "Disabled"}</span>
        </label>
      </div>

      <div className="p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Current key</div>
            <div className="font-mono text-sm bg-gray-50 border border-gray-200 p-2">
              {cfg.has_key ? cfg.api_key_masked : <span className="text-gray-400">Not set</span>}
            </div>
          </div>
          <div className="space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Last successful pull</div>
            <div className="text-sm bg-gray-50 border border-gray-200 p-2">
              {lastSuccess}
              {cfg.last_date_from && <span className="text-[10px] text-gray-500 ml-2">(date_from: {cfg.last_date_from})</span>}
              {typeof cfg.last_created_count === "number" && (
                <span className="ml-2 text-[10px] uppercase tracking-widest text-[#008A00] font-bold">+{cfg.last_created_count} new</span>
              )}
            </div>
            {cfg.last_error && <div className="text-[11px] text-[#E60000] font-mono bg-[#FFE9E9] p-2 border border-[#E60000]">⚠ {cfg.last_error}</div>}
          </div>
        </div>

        <label className="block">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">API key (rotate anytime)</div>
          <div className="flex items-stretch gap-2">
            <input
              type={reveal ? "text" : "password"}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={cfg.has_key ? "Paste new key to replace, or leave blank" : "Paste ExportersIndia API key (e.g. RFVOVXlpV2NlQVMvVzl4Wk92VkcwUT09)"}
              className="flex-1 border border-gray-300 px-3 py-2 text-sm font-mono"
              data-testid="ei-pull-apikey-input"
            />
            <button type="button" onClick={() => setReveal(v => !v)} className="border border-gray-300 px-3 py-2 text-gray-700 hover:bg-gray-50" title={reveal ? "Hide" : "Reveal"}>
              {reveal ? <EyeSlash size={16} weight="bold" /> : <Eye size={16} weight="bold" />}
            </button>
            <button type="button" onClick={() => save({ api_key: apiKey })} disabled={!apiKey.trim() || saving}
              className="bg-[#002FA7] hover:bg-[#002288] text-white px-4 py-2 text-[10px] uppercase tracking-widest font-bold flex items-center gap-1 disabled:opacity-50"
              data-testid="ei-pull-save-key-btn">
              <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save key"}
            </button>
          </div>
        </label>

        <label className="block">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Account email</div>
          <div className="flex items-stretch gap-2">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="e.g. citspray@gmail.com"
              className="flex-1 border border-gray-300 px-3 py-2 text-sm"
              data-testid="ei-pull-email-input"
            />
            <button type="button" onClick={() => save({ email })} disabled={saving}
              className="border border-gray-900 hover:bg-gray-900 hover:text-white px-3 py-2 text-[10px] uppercase tracking-widest font-bold" data-testid="ei-pull-save-email-btn">
              Save email
            </button>
          </div>
        </label>

        <div className="block">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Pull interval</div>
          <div className="flex items-stretch gap-2 flex-wrap">
            <div className="flex items-center gap-1">
              <input type="number" min="0" max="60" value={mins} onChange={(e) => setMins(Number(e.target.value))}
                className="w-20 border border-gray-300 px-2 py-2 text-sm" data-testid="ei-pull-mins-input" />
              <span className="text-xs text-gray-500">min</span>
            </div>
            <div className="flex items-center gap-1">
              <input type="number" min="0" max="59" value={secs} onChange={(e) => setSecs(Number(e.target.value))}
                className="w-20 border border-gray-300 px-2 py-2 text-sm" data-testid="ei-pull-secs-input" />
              <span className="text-xs text-gray-500">sec</span>
            </div>
            <button type="button" onClick={() => save({ interval_minutes: mins, interval_seconds: secs })} disabled={saving}
              className="bg-[#002FA7] hover:bg-[#002288] text-white px-4 py-2 text-[10px] uppercase tracking-widest font-bold" data-testid="ei-pull-save-interval-btn">
              Save interval
            </button>
            <span className="text-[10px] text-gray-500 self-center">Minimum 10s. Current: every {cfg.interval_minutes}m {cfg.interval_seconds}s.</span>
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-gray-200 pt-4">
          <div className="text-[11px] text-gray-500 font-mono break-all">
            GET {cfg.pull_url}?k=…&amp;email={email || "…"}&amp;date_from=YYYY-MM-DD
          </div>
          <button type="button" onClick={runNow} disabled={running || !cfg.has_key || !email}
            className="bg-[#25D366] hover:bg-[#1da851] text-white px-4 py-2 text-[10px] uppercase tracking-widest font-bold flex items-center gap-1 disabled:opacity-50"
            data-testid="ei-pull-run-now-btn">
            <Lightning size={14} weight="bold" /> {running ? "Pulling…" : "Run pull now"}
          </button>
        </div>
      </div>
    </div>
  );
}




// ---------------- TradeIndia pull (admin) ----------------
function TradeIndiaPanel() {
  const [cfg, setCfg] = useState(null);
  const [userid, setUserid] = useState("");
  const [profileId, setProfileId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [mins, setMins] = useState(5);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/settings/tradeindia-pull");
      setCfg(data); setUserid(data.userid || ""); setProfileId(data.profile_id || ""); setMins(data.interval_minutes || 5); setApiKey("");
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setSaving(true);
    try { await api.put("/settings/tradeindia-pull", patch); toast.success("Saved"); await load(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };
  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/settings/tradeindia-pull/run-now");
      if (data.ok === false) toast.error(`TradeIndia: ${data.error}`);
      else toast.success(`Pulled ${data.received ?? 0} enquiries — ${(data.created || []).length} new lead(s), ${data.repeat_enquiries || 0} repeat`);
      await load();
    } catch (e) { toast.error(errMsg(e, "Pull failed")); }
    finally { setRunning(false); }
  };

  if (!cfg) return null;
  const configured = cfg.userid && cfg.profile_id && cfg.has_key;
  return (
    <div className="border border-gray-200 bg-white" data-testid="tradeindia-panel">
      <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between gap-3">
        <div>
          <h2 className="font-chivo font-bold text-lg flex items-center gap-2"><ShieldCheck size={18} weight="bold" /> TradeIndia Pull API</h2>
          <p className="text-xs text-gray-500 mt-1 max-w-2xl">
            LeadOrbit checks TradeIndia's "My Inquiry" API on this interval and imports new enquiries as leads
            (source TradeIndia, assigned by round-robin like the other portals). Each enquiry is imported once.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="ti-enable-toggle">
          <input type="checkbox" checked={cfg.enabled} disabled={!configured}
            onChange={() => save({ enabled: !cfg.enabled })} className="w-4 h-4 accent-[#25D366]" />
          <span className="text-[10px] uppercase tracking-widest font-bold">{cfg.enabled ? "Enabled" : "Disabled"}</span>
        </label>
      </div>
      <div className="p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">User ID</div>
            <input value={userid} onChange={(e) => setUserid(e.target.value)} className="w-full border border-gray-300 px-3 py-2 text-sm font-mono" data-testid="ti-userid" />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Profile ID</div>
            <input value={profileId} onChange={(e) => setProfileId(e.target.value)} className="w-full border border-gray-300 px-3 py-2 text-sm font-mono" data-testid="ti-profile" />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Key {cfg.has_key && <span className="normal-case tracking-normal font-mono text-gray-400">({cfg.api_key_masked})</span>}</div>
            <input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder={cfg.has_key ? "Leave blank to keep" : "Paste key"}
              className="w-full border border-gray-300 px-3 py-2 text-sm font-mono" data-testid="ti-key" />
          </label>
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Check every (minutes)</div>
            <input type="number" min="1" max="120" value={mins} onChange={(e) => setMins(Number(e.target.value))} className="w-24 border border-gray-300 px-2 py-2 text-sm" />
          </label>
          <button type="button" disabled={saving}
            onClick={() => save({ userid, profile_id: profileId, api_key: apiKey || undefined, interval_minutes: mins })}
            className="bg-[#002FA7] hover:bg-[#002288] text-white px-4 py-2 text-[10px] uppercase tracking-widest font-bold flex items-center gap-1 disabled:opacity-50" data-testid="ti-save">
            <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save"}
          </button>
          <button type="button" onClick={runNow} disabled={running || !configured}
            className="bg-[#25D366] hover:bg-[#1da851] text-white px-4 py-2 text-[10px] uppercase tracking-widest font-bold flex items-center gap-1 disabled:opacity-50" data-testid="ti-run-now">
            <Lightning size={14} weight="bold" /> {running ? "Pulling…" : "Run pull now"}
          </button>
        </div>
        <div className="text-xs bg-gray-50 border border-gray-200 p-2 space-y-1">
          <div>Last successful pull: <b>{cfg.last_success_at ? fmtIST(cfg.last_success_at) : "Never"}</b>
            {typeof cfg.last_received_count === "number" && <span className="ml-2 text-gray-500">({cfg.last_received_count} received, +{cfg.last_created_count || 0} new)</span>}
          </div>
          {cfg.last_pulled_at && <div className="text-gray-500">Last attempt: {fmtIST(cfg.last_pulled_at)}</div>}
          {cfg.last_unrecognised_count > 0 && <div className="text-[#B85F00]">{cfg.last_unrecognised_count} enquiries had no phone/email and were skipped.</div>}
          {cfg.last_error && <div className="text-[11px] text-[#E60000] font-mono bg-[#FFE9E9] p-2 border border-[#E60000]">⚠ {cfg.last_error}</div>}
        </div>
      </div>
    </div>
  );
}


// ---------------- Buyleads Routing (admin) ----------------
function BuyleadsRoutingPanel() {
  const [configs, setConfigs] = useState([]);
  const [execs, setExecs] = useState([]);
  const [draft, setDraft] = useState({}); // { IndiaMART: {mode, agent_ids}, ExportersIndia: {...} }
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/settings/buyleads-routing");
      setConfigs(data.configs || []);
      setExecs(data.executives || []);
      const d = {};
      (data.configs || []).forEach((c) => {
        d[c.source] = { mode: c.mode || "all", agent_ids: c.agent_ids || [] };
      });
      setDraft(d);
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const toggleAgent = (source, agentId) => {
    setDraft((d) => {
      const cur = d[source] || { mode: "selected", agent_ids: [] };
      const has = cur.agent_ids.includes(agentId);
      return {
        ...d,
        [source]: { ...cur, agent_ids: has ? cur.agent_ids.filter((x) => x !== agentId) : [...cur.agent_ids, agentId] },
      };
    });
  };

  const save = async (source) => {
    const cur = draft[source];
    if (!cur) return;
    if (cur.mode === "selected" && cur.agent_ids.length === 0) {
      toast.error("Select at least one agent — or switch to All agents");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/settings/buyleads-routing/${source}`, cur);
      toast.success(`${source} buyleads routing saved`);
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const setMode = (source, mode) => {
    setDraft((d) => ({ ...d, [source]: { ...(d[source] || { agent_ids: [] }), mode } }));
  };

  return (
    <div className="border border-gray-200 bg-white" data-testid="buyleads-routing-panel">
      <div className="px-5 py-4 border-b border-gray-200">
        <h2 className="font-chivo font-bold text-lg flex items-center gap-2">
          <UsersIcon size={18} weight="bold" /> Buyleads Routing
        </h2>
        <p className="text-xs text-gray-500 mt-1 leading-relaxed">
          Route high-intent buyleads to a specific set of agents per source (round-robin).
          IndiaMART buyleads = <span className="kbd">QUERY_TYPE = B</span>.
          ExportersIndia buyleads = <span className="kbd">inq_type = "buyleads"</span>.
          All other leads continue to use the default round-robin.
        </p>
      </div>
      <div className="divide-y divide-gray-200">
        {configs.map((cfg) => {
          const d = draft[cfg.source] || { mode: "all", agent_ids: [] };
          return (
            <div key={cfg.source} className="px-5 py-4" data-testid={`buyleads-${cfg.source}`}>
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <div className="font-chivo font-bold">{cfg.source}</div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setMode(cfg.source, "all")}
                    className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold ${d.mode === "all" ? "bg-[#002FA7] text-white" : "border border-gray-300 hover:bg-gray-100"}`}
                    data-testid={`buyleads-mode-all-${cfg.source}`}
                  >All agents</button>
                  <button
                    onClick={() => setMode(cfg.source, "selected")}
                    className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold ${d.mode === "selected" ? "bg-[#002FA7] text-white" : "border border-gray-300 hover:bg-gray-100"}`}
                    data-testid={`buyleads-mode-selected-${cfg.source}`}
                  >Selected agents</button>
                </div>
              </div>
              {d.mode === "selected" && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {execs.length === 0 && <span className="text-xs text-gray-400 italic">No active executives yet.</span>}
                  {execs.map((e) => {
                    const on = (d.agent_ids || []).includes(e.id);
                    return (
                      <button
                        key={e.id}
                        onClick={() => toggleAgent(cfg.source, e.id)}
                        className={`px-3 py-1.5 text-xs border ${on ? "bg-[#25D366] text-white border-[#25D366]" : "border-gray-300 hover:bg-gray-50"}`}
                        data-testid={`buyleads-agent-${cfg.source}-${e.username}`}
                      >
                        {on ? "✓ " : ""}{e.name} <span className="text-[10px] opacity-70">@{e.username}</span>
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-[10px] uppercase tracking-widest text-gray-400 font-bold">
                  {d.mode === "selected"
                    ? `${d.agent_ids.length} agent${d.agent_ids.length === 1 ? "" : "s"} selected`
                    : "Using default round-robin across all active executives"}
                  {cfg.updated_at && <span className="ml-2 normal-case tracking-normal">· last saved {fmtIST(cfg.updated_at)}</span>}
                </div>
                <button
                  onClick={() => save(cfg.source)}
                  disabled={busy}
                  className="bg-[#002FA7] hover:bg-[#002288] text-white px-4 py-2 text-[10px] uppercase tracking-widest font-bold flex items-center gap-1 disabled:opacity-50"
                  data-testid={`buyleads-save-${cfg.source}`}
                >
                  <FloppyDisk size={12} weight="bold" /> Save
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------- Leave / Holiday management (admin) ----------------
function LeaveManagementPanel() {
  const [leaves, setLeaves] = useState([]);
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ user_id: "", start_date: "", end_date: "", reason: "" });
  const [editId, setEditId] = useState(null);
  const [edit, setEdit] = useState({ start_date: "", end_date: "", reason: "" });

  const today = new Date().toISOString().slice(0, 10);

  const load = async () => {
    try {
      const [{ data: lvs }, { data: us }] = await Promise.all([
        api.get("/leaves"),
        api.get("/users"),
      ]);
      setLeaves(lvs || []);
      setUsers((us || []).filter((u) => u.role === "executive"));
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const addLeave = async (e) => {
    e.preventDefault();
    if (!form.user_id || !form.start_date || !form.end_date) {
      toast.error("Agent and dates are required");
      return;
    }
    if (form.start_date > form.end_date) {
      toast.error("Start date cannot be after end date");
      return;
    }
    setBusy(true);
    try {
      await api.post("/leaves", form);
      toast.success("Leave saved — agent will be logged out on next poll if active");
      setForm({ user_id: "", start_date: "", end_date: "", reason: "" });
      await load();
    } catch (e2) { toast.error(errMsg(e2)); }
    finally { setBusy(false); }
  };

  const cancelLeave = async (id) => {
    if (!window.confirm("Cancel this leave? The agent will regain access immediately.")) return;
    setBusy(true);
    try {
      await api.post(`/leaves/${id}/cancel`);
      toast.success("Leave cancelled");
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const startEdit = (lv) => {
    setEditId(lv.id);
    setEdit({ start_date: lv.start_date, end_date: lv.end_date, reason: lv.reason || "" });
  };

  const saveEdit = async (id) => {
    if (edit.start_date > edit.end_date) { toast.error("Start date cannot be after end date"); return; }
    setBusy(true);
    try {
      await api.patch(`/leaves/${id}`, edit);
      toast.success("Leave updated");
      setEditId(null);
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const active = leaves.filter((l) => l.is_active);
  const upcoming = leaves.filter((l) => !l.is_active && l.start_date > today);
  const past = leaves.filter((l) => !l.is_active && l.end_date < today);

  const renderRow = (lv) => (
    <div key={lv.id} className="px-5 py-3 flex flex-wrap items-center gap-3 border-b border-gray-100" data-testid={`leave-row-${lv.id}`}>
      <div className="flex-1 min-w-[200px]">
        <div className="font-semibold text-sm">
          {lv.user_name || lv.user_username || lv.user_id}
          {lv.is_active && <span className="ml-2 bg-[#E60000] text-white px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest">On leave</span>}
        </div>
        <div className="text-[10px] uppercase tracking-widest text-gray-500 font-mono">@{lv.user_username}</div>
      </div>
      {editId === lv.id ? (
        <>
          <input type="date" value={edit.start_date} onChange={(e) => setEdit((s) => ({ ...s, start_date: e.target.value }))} className="border border-gray-300 px-2 py-1 text-xs" data-testid={`edit-start-${lv.id}`} />
          <span className="text-gray-400">→</span>
          <input type="date" value={edit.end_date} onChange={(e) => setEdit((s) => ({ ...s, end_date: e.target.value }))} className="border border-gray-300 px-2 py-1 text-xs" data-testid={`edit-end-${lv.id}`} />
          <input type="text" placeholder="reason" value={edit.reason} onChange={(e) => setEdit((s) => ({ ...s, reason: e.target.value }))} className="border border-gray-300 px-2 py-1 text-xs flex-1 min-w-[140px]" data-testid={`edit-reason-${lv.id}`} />
          <button onClick={() => saveEdit(lv.id)} disabled={busy} className="bg-[#002FA7] hover:bg-[#002288] text-white px-2 py-1 text-[10px] uppercase tracking-widest font-bold" data-testid={`edit-save-${lv.id}`}>Save</button>
          <button onClick={() => setEditId(null)} className="border border-gray-300 px-2 py-1 text-[10px] uppercase tracking-widest font-bold">Cancel</button>
        </>
      ) : (
        <>
          <div className="text-xs font-mono">{lv.start_date} → {lv.end_date}</div>
          {lv.reason && <div className="text-xs text-gray-600 italic max-w-xs truncate">"{lv.reason}"</div>}
          <div className="flex items-center gap-2">
            <button onClick={() => startEdit(lv)} disabled={busy} className="border border-gray-300 hover:bg-gray-100 px-2 py-1 text-[10px] uppercase tracking-widest font-bold" data-testid={`leave-edit-${lv.id}`}>
              Modify
            </button>
            <button onClick={() => cancelLeave(lv.id)} disabled={busy} className="text-[#E60000] hover:bg-[#E60000] hover:text-white border border-[#E60000] px-2 py-1 text-[10px] uppercase tracking-widest font-bold" data-testid={`leave-cancel-${lv.id}`}>
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="border border-gray-200 bg-white" data-testid="leave-panel">
      <div className="px-5 py-4 border-b border-gray-200">
        <h2 className="font-chivo font-bold text-lg flex items-center gap-2">
          <Calendar size={18} weight="bold" /> Leave / Holiday Management
        </h2>
        <p className="text-xs text-gray-500 mt-1 leading-relaxed">
          Mark executives on leave. They won't receive any new leads (auto / manual / PNS) and are
          soft-logged-out on their next API call. Past, current and future-dated leaves are all
          supported — modify or cancel any time.
        </p>
      </div>

      <form onSubmit={addLeave} className="px-5 py-4 border-b border-gray-200 bg-gray-50 grid grid-cols-1 md:grid-cols-5 gap-2 items-end" data-testid="leave-add-form">
        <label className="block">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Agent</div>
          <select required value={form.user_id} onChange={(e) => setForm((s) => ({ ...s, user_id: e.target.value }))} className="w-full border border-gray-300 px-2 py-2 text-sm" data-testid="leave-user-select">
            <option value="">— Select agent —</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name} (@{u.username})</option>)}
          </select>
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Start</div>
          <input required type="date" value={form.start_date} onChange={(e) => setForm((s) => ({ ...s, start_date: e.target.value }))} className="w-full border border-gray-300 px-2 py-2 text-sm" data-testid="leave-start-date" />
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">End</div>
          <input required type="date" value={form.end_date} onChange={(e) => setForm((s) => ({ ...s, end_date: e.target.value }))} className="w-full border border-gray-300 px-2 py-2 text-sm" data-testid="leave-end-date" />
        </label>
        <label className="block md:col-span-1">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Reason (optional)</div>
          <input type="text" value={form.reason} onChange={(e) => setForm((s) => ({ ...s, reason: e.target.value }))} placeholder="e.g. wedding" className="w-full border border-gray-300 px-2 py-2 text-sm" data-testid="leave-reason" />
        </label>
        <button type="submit" disabled={busy} className="bg-[#002FA7] hover:bg-[#002288] text-white px-4 py-2 text-[10px] uppercase tracking-widest font-bold flex items-center justify-center gap-1 disabled:opacity-50" data-testid="leave-add-btn">
          <Plus size={12} weight="bold" /> Add leave
        </button>
      </form>

      {active.length > 0 && (
        <div>
          <div className="px-5 pt-3 pb-1 text-[10px] uppercase tracking-widest text-[#E60000] font-bold">Active now ({active.length})</div>
          {active.map(renderRow)}
        </div>
      )}
      {upcoming.length > 0 && (
        <div>
          <div className="px-5 pt-3 pb-1 text-[10px] uppercase tracking-widest text-[#002FA7] font-bold">Upcoming ({upcoming.length})</div>
          {upcoming.map(renderRow)}
        </div>
      )}
      {past.length > 0 && (
        <div>
          <div className="px-5 pt-3 pb-1 text-[10px] uppercase tracking-widest text-gray-400 font-bold">Past ({past.length})</div>
          {past.map(renderRow)}
        </div>
      )}
      {leaves.length === 0 && (
        <div className="px-5 py-8 text-xs uppercase tracking-widest text-gray-400 font-bold text-center">No leaves on record yet.</div>
      )}
    </div>
  );
}

function EmailAutoSendPanel() {
  const [smtp, setSmtp] = useState(null);
  const [smtpForm, setSmtpForm] = useState({ host: "", port: 465, security: "ssl", email: "", password: "", from_name: "", enabled: false });
  const [revealPw, setRevealPw] = useState(false);
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [tpl, setTpl] = useState(null);
  const [tplForm, setTplForm] = useState({ subject: "", body: "", attachments: [] });
  const [savingTpl, setSavingTpl] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [uploadingAtt, setUploadingAtt] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const fileRef = React.useRef(null);

  // Detect HTML in the current draft and produce a preview with sample
  // variable substitution. Same heuristic as the backend.
  const bodyIsHtml = React.useMemo(() => {
    const s = (tplForm.body || "").toLowerCase();
    return /<\s*(html|body|table|div|p|h[1-6]|br|img|a|span|strong|em|ul|ol|li|tr|td|tbody)[\s>]/.test(s);
  }, [tplForm.body]);
  const previewHtml = React.useMemo(() => {
    const sample = { name: "Akash", requirement: "Pumps", phone: "+91 99999 00001", email: "akash@example.com", source: "Manual" };
    const sub = (s) => (s || "")
      .replaceAll("{{name}}", sample.name)
      .replaceAll("{{requirement}}", sample.requirement)
      .replaceAll("{{phone}}", sample.phone)
      .replaceAll("{{email}}", sample.email)
      .replaceAll("{{source}}", sample.source);
    return sub(tplForm.body);
  }, [tplForm.body]);

  const load = async () => {
    try {
      const [{ data: s }, { data: t }] = await Promise.all([
        api.get("/settings/email"),
        api.get("/settings/email-template"),
      ]);
      setSmtp(s);
      setSmtpForm({
        host: s.host || "smtp.hostinger.com",
        port: s.port || 465,
        security: s.security || "ssl",
        email: s.email || "",
        password: "", // never prefill — keeps current if blank
        from_name: s.from_name || "",
        enabled: !!s.enabled,
      });
      setTpl(t);
      setTplForm({
        subject: t.subject || "",
        body: t.body || "",
        attachments: t.attachments || [],
      });
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const saveSmtp = async (e) => {
    e?.preventDefault?.();
    setSavingSmtp(true);
    try {
      const patch = { ...smtpForm };
      if (!patch.password) delete patch.password; // keep existing
      patch.port = Number(patch.port) || 465;
      await api.put("/settings/email", patch);
      toast.success("SMTP settings saved");
      await load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSavingSmtp(false); }
  };

  const saveTpl = async (e) => {
    e?.preventDefault?.();
    setSavingTpl(true);
    try {
      await api.put("/settings/email-template", tplForm);
      toast.success("Template saved");
      await load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSavingTpl(false); }
  };

  const onAttach = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("Max 25 MB per attachment"); return; }
    setUploadingAtt(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "document");
      const { data } = await api.post("/chatflows/upload-media", fd);
      // Use stored_name directly from the API response — don't parse the URL.
      const stored = data.stored_name;
      const next = [
        ...(tplForm.attachments || []),
        { stored_name: stored, original_filename: file.name, mime_type: file.type || "application/octet-stream" },
      ];
      setTplForm((f) => ({ ...f, attachments: next }));
      // Persist immediately so the attachment survives a page refresh / SMTP save reload.
      // Sends current subject/body together so we don't accidentally clear them.
      try {
        await api.put("/settings/email-template", { subject: tplForm.subject, body: tplForm.body, attachments: next });
        toast.success(`${file.name} attached and saved`);
      } catch (saveErr) {
        toast.error(errMsg(saveErr, "Attached locally but auto-save failed — click Save template"));
      }
    } catch (err) { toast.error(errMsg(err, "Upload failed")); }
    finally {
      setUploadingAtt(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeAtt = async (idx) => {
    const next = (tplForm.attachments || []).filter((_, i) => i !== idx);
    setTplForm((f) => ({ ...f, attachments: next }));
    // Persist immediately so the removal survives a page refresh.
    try {
      await api.put("/settings/email-template", { subject: tplForm.subject, body: tplForm.body, attachments: next });
      toast.success("Attachment removed");
    } catch (err) {
      toast.error(errMsg(err, "Removed locally but auto-save failed — click Save template"));
    }
  };

  const testSend = async () => {
    if (!testTo.trim()) { toast.error("Enter a recipient email"); return; }
    setSendingTest(true);
    try {
      await api.post("/settings/email/test-send", { to: testTo.trim() });
      toast.success(`Test email sent to ${testTo.trim()}`);
    } catch (err) { toast.error(errMsg(err, "Test send failed")); }
    finally { setSendingTest(false); }
  };

  if (!smtp || !tpl) return <div className="border border-gray-200 bg-white p-5 text-xs uppercase tracking-widest text-gray-400">Loading email settings…</div>;

  return (
    <div className="border border-gray-200 bg-white" data-testid="email-autosend-panel">
      <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h2 className="font-chivo font-bold text-lg flex items-center gap-2"><EnvelopeSimple size={18} weight="bold" /> Email Auto-Send</h2>
          <p className="text-xs text-gray-500 mt-1 max-w-2xl">
            Sends a configured email to every lead's email address — at lead creation AND whenever a new email is added later.
            Each address is mailed exactly once per lead.
          </p>
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest font-bold ${smtpForm.enabled ? "bg-[#008A00] text-white" : "bg-gray-200 text-gray-700"}`}>
          {smtpForm.enabled ? "Enabled" : "Disabled"}
        </span>
      </div>

      {/* SMTP form */}
      <form onSubmit={saveSmtp} className="px-5 py-4 grid grid-cols-1 md:grid-cols-2 gap-4 border-b border-gray-200">
        <label className="block">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">SMTP Host</div>
          <input value={smtpForm.host} onChange={(e) => setSmtpForm((f) => ({ ...f, host: e.target.value }))}
            placeholder="smtp.hostinger.com"
            className="w-full border border-gray-300 px-3 py-2 text-sm font-mono"
            data-testid="email-smtp-host" />
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Port</div>
          <input type="number" value={smtpForm.port} onChange={(e) => setSmtpForm((f) => ({ ...f, port: e.target.value }))}
            className="w-full border border-gray-300 px-3 py-2 text-sm font-mono" data-testid="email-smtp-port" />
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Security</div>
          <select value={smtpForm.security} onChange={(e) => setSmtpForm((f) => ({ ...f, security: e.target.value }))}
            className="w-full border border-gray-300 px-3 py-2 text-sm" data-testid="email-smtp-security">
            <option value="ssl">SSL (465)</option>
            <option value="tls">TLS / STARTTLS (587)</option>
            <option value="none">None</option>
          </select>
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">From email</div>
          <input type="email" value={smtpForm.email} onChange={(e) => setSmtpForm((f) => ({ ...f, email: e.target.value }))}
            placeholder="aroma@citspray.com"
            className="w-full border border-gray-300 px-3 py-2 text-sm font-mono" data-testid="email-smtp-email" />
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">From name (optional)</div>
          <input value={smtpForm.from_name} onChange={(e) => setSmtpForm((f) => ({ ...f, from_name: e.target.value }))}
            placeholder="Aroma"
            className="w-full border border-gray-300 px-3 py-2 text-sm" data-testid="email-smtp-fromname" />
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1 flex items-center justify-between">
            <span>Password</span>
            {smtp.has_password && <span className="text-gray-400 normal-case">current: <span className="font-mono">{smtp.password_masked}</span></span>}
          </div>
          <div className="flex items-center gap-2">
            <input type={revealPw ? "text" : "password"} value={smtpForm.password}
              onChange={(e) => setSmtpForm((f) => ({ ...f, password: e.target.value }))}
              placeholder={smtp.has_password ? "Leave blank to keep current" : "Enter password"}
              className="flex-1 border border-gray-300 px-3 py-2 text-sm font-mono"
              data-testid="email-smtp-password" />
            <button type="button" onClick={() => setRevealPw((v) => !v)} className="border border-gray-300 p-2" title="Reveal">
              {revealPw ? <EyeSlash size={14} /> : <Eye size={14} />}
            </button>
          </div>
        </label>
        <label className="md:col-span-2 flex items-center gap-2">
          <input type="checkbox" checked={smtpForm.enabled} onChange={(e) => setSmtpForm((f) => ({ ...f, enabled: e.target.checked }))} data-testid="email-smtp-enabled" />
          <span className="text-sm">Enable auto-send</span>
        </label>
        <div className="md:col-span-2 flex items-center justify-end gap-2">
          <button type="submit" disabled={savingSmtp} className="bg-[#002FA7] hover:bg-[#002288] text-white px-4 py-2 text-[10px] uppercase tracking-widest font-bold flex items-center gap-1 disabled:opacity-50" data-testid="email-smtp-save-btn">
            <FloppyDisk size={12} weight="bold" /> {savingSmtp ? "Saving…" : "Save SMTP"}
          </button>
        </div>
      </form>

      {/* Template form */}
      <form onSubmit={saveTpl} className="px-5 py-4 space-y-3 border-b border-gray-200">
        <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Email template</div>
        <div className="text-xs text-gray-500">
          Variables: <span className="kbd">{"{{name}}"}</span> <span className="kbd">{"{{requirement}}"}</span> <span className="kbd">{"{{phone}}"}</span> <span className="kbd">{"{{email}}"}</span> <span className="kbd">{"{{source}}"}</span>
          <span className="ml-2 text-gray-400">— HTML supported (auto-detected). Variables work inside HTML too.</span>
        </div>
        <label className="block">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Subject</div>
          <input value={tplForm.subject} onChange={(e) => setTplForm((f) => ({ ...f, subject: e.target.value }))}
            placeholder="Thank you for your enquiry, {{name}}"
            className="w-full border border-gray-300 px-3 py-2 text-sm" data-testid="email-tpl-subject" />
        </label>
        <div>
          <div className="flex items-center justify-between mb-1">
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Body</div>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => setShowPreview(false)} className={`px-2 py-1 text-[10px] uppercase tracking-widest font-bold border ${!showPreview ? "bg-gray-900 text-white border-gray-900" : "border-gray-300 hover:bg-gray-100"}`} data-testid="email-tpl-tab-edit">Edit</button>
              <button type="button" onClick={() => setShowPreview(true)} className={`px-2 py-1 text-[10px] uppercase tracking-widest font-bold border ${showPreview ? "bg-gray-900 text-white border-gray-900" : "border-gray-300 hover:bg-gray-100"}`} data-testid="email-tpl-tab-preview">Preview</button>
            </div>
          </div>
          {showPreview ? (
            <div className="border border-gray-300 bg-white" data-testid="email-tpl-preview">
              {bodyIsHtml ? (
                <iframe
                  title="email-preview"
                  srcDoc={previewHtml}
                  sandbox=""
                  className="w-full"
                  style={{ height: 480, border: 0 }}
                  data-testid="email-tpl-preview-iframe"
                />
              ) : (
                <div className="p-4 text-sm whitespace-pre-wrap font-mono">{previewHtml || <span className="text-gray-400 italic">(empty)</span>}</div>
              )}
              <div className="px-3 py-1.5 border-t border-gray-200 text-[10px] uppercase tracking-widest text-gray-400 bg-gray-50">
                Detected: {bodyIsHtml ? "HTML" : "Plain text"} · variables substituted with sample values (Akash, Pumps, etc.)
              </div>
            </div>
          ) : (
            <textarea rows={12} value={tplForm.body} onChange={(e) => setTplForm((f) => ({ ...f, body: e.target.value }))}
              placeholder="Hi {{name}},&#10;&#10;Thanks for your enquiry about {{requirement}}…&#10;&#10;HTML works too — paste full <table>…</table> markup."
              className="w-full border border-gray-300 px-3 py-2 text-sm font-mono whitespace-pre-wrap"
              data-testid="email-tpl-body" />
          )}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Attachments ({tplForm.attachments.length})</div>
          {tplForm.attachments.length > 0 && (
            <ul className="space-y-1 mb-2" data-testid="email-tpl-attachments">
              {tplForm.attachments.map((a, i) => (
                <li key={i} className="flex items-center gap-2 text-xs border border-gray-200 px-2 py-1">
                  <Paperclip size={11} className="text-[#475569] shrink-0" />
                  <span className="font-mono truncate flex-1">{a.original_filename || a.stored_name}</span>
                  <button type="button" onClick={() => removeAtt(i)} className="text-[#E60000] hover:underline text-[10px] uppercase tracking-widest font-bold" data-testid={`email-tpl-att-remove-${i}`}>
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
          <input ref={fileRef} type="file" onChange={onAttach} disabled={uploadingAtt} className="hidden" id="email-tpl-att-file" data-testid="email-tpl-att-input" />
          <label htmlFor="email-tpl-att-file" className="inline-block border border-gray-300 hover:border-gray-900 cursor-pointer px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold">
            {uploadingAtt ? "Uploading…" : "Add attachment"}
          </label>
        </div>
        <div className="flex items-center justify-end gap-2">
          <button type="submit" disabled={savingTpl} className="bg-[#002FA7] hover:bg-[#002288] text-white px-4 py-2 text-[10px] uppercase tracking-widest font-bold flex items-center gap-1 disabled:opacity-50" data-testid="email-tpl-save-btn">
            <FloppyDisk size={12} weight="bold" /> {savingTpl ? "Saving…" : "Save template"}
          </button>
        </div>
      </form>

      {/* Test send */}
      <div className="px-5 py-4 flex items-center gap-2 flex-wrap bg-gray-50">
        <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mr-1">Test send:</div>
        <input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="recipient@example.com"
          className="border border-gray-300 px-3 py-2 text-sm font-mono flex-1 min-w-[220px]" data-testid="email-test-to" />
        <button onClick={testSend} disabled={sendingTest || !smtpForm.email} className="border border-gray-900 hover:bg-gray-900 hover:text-white px-3 py-2 text-[10px] uppercase tracking-widest font-bold flex items-center gap-1 disabled:opacity-50" data-testid="email-test-send-btn">
          <PaperPlaneTilt size={12} weight="bold" /> Send test
        </button>
      </div>
    </div>
  );
}

// ---------------- WhatsApp Auto-Sequence ----------------
function WhatsAppAutoSequencePanel() {
  const [cfg, setCfg] = useState(null);
  const [quickReplies, setQuickReplies] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [selectedAdd, setSelectedAdd] = useState("");
  const [customText, setCustomText] = useState("");

  const load = async () => {
    try {
      const [{ data: seq }, { data: qrs }, { data: tpls }] = await Promise.all([
        api.get("/settings/auto-sequence"),
        api.get("/quick-replies"),
        api.get("/whatsapp/templates")
      ]);
      setCfg(seq);
      setQuickReplies(qrs || []);
      setTemplates(tpls || []);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    const next = { ...cfg, ...patch };
    setSaving(true);
    try {
      await api.put("/settings/auto-sequence", next);
      toast.success("Auto-sequence settings saved");
      setCfg(next);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const addQr = (value) => {
    if (!value) return;
    save({ quick_reply_ids: [...cfg.quick_reply_ids, value] });
    setSelectedAdd("");
  };

  const addCustom = () => {
    const text = customText.trim();
    if (!text) return;
    save({ quick_reply_ids: [...cfg.quick_reply_ids, `__custom__:${text}`] });
    setCustomText("");
  };

  const removeQr = (index) => {
    const next = [...cfg.quick_reply_ids];
    next.splice(index, 1);
    save({ quick_reply_ids: next });
  };

  if (!cfg) return null;

  return (
    <div className="border border-gray-200 bg-white" data-testid="auto-sequence-panel">
      <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between gap-3">
        <div>
          <h2 className="font-chivo font-bold text-lg flex items-center gap-2">
            <PaperPlaneTilt size={18} weight="bold" /> WhatsApp Auto-Reply Sequence
          </h2>
          <p className="text-xs text-gray-500 mt-1 max-w-2xl">
            Automatically send a sequence of Quick Replies or WhatsApp Templates when a customer replies for the very first time.
            This triggers ONLY ONCE per customer conversation (lead) and opens the 24-hour messaging window.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={cfg.is_active} onChange={(e) => save({ is_active: e.target.checked })} className="w-4 h-4 accent-[#25D366]" />
          <span className="text-[10px] uppercase tracking-widest font-bold">{cfg.is_active ? "Enabled" : "Disabled"}</span>
        </label>
      </div>

      <div className="p-5">
        <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-2">Message Sequence</div>
        
        {cfg.quick_reply_ids.length === 0 ? (
          <div className="text-sm text-gray-500 italic mb-4">No messages in sequence. Add one below.</div>
        ) : (
          <div className="space-y-2 mb-4">
            {cfg.quick_reply_ids.map((id, i) => {
              const isCustom = id.startsWith("__custom__:");
              const customMsg = isCustom ? id.slice("__custom__:".length) : null;
              const qr = !isCustom ? quickReplies.find(q => q.id === id) : null;
              const tpl = !isCustom && !qr ? templates.find(t => t.id === id || t.name === id) : null;
              return (
                <div key={`${id}-${i}`} className="flex items-center justify-between border border-gray-200 p-2 bg-gray-50 text-sm">
                  <div className="flex-1 min-w-0 mr-3">
                    <span className="font-bold mr-2">{i + 1}.</span>
                    {isCustom ? (
                      <span className="inline-flex items-center gap-1.5">
                        <span className="text-[9px] bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded font-bold uppercase tracking-wider">Custom</span>
                        <span className="text-gray-700 truncate">{customMsg}</span>
                      </span>
                    ) : qr ? (
                      <span className="inline-flex items-center gap-1.5">
                        <span className="text-[9px] bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded font-bold uppercase tracking-wider">Quick Reply</span>
                        <strong>{qr.title}</strong>
                      </span>
                    ) : tpl ? (
                      <span className="inline-flex items-center gap-1.5">
                        <span className="text-[9px] bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded font-bold uppercase tracking-wider">WA Template</span>
                        <strong>{tpl.name}</strong>
                      </span>
                    ) : (
                      <span className="text-[#E60000] font-bold">Unknown/Deleted Item</span>
                    )}
                  </div>
                  <button onClick={() => removeQr(i)} disabled={saving} className="text-[#E60000] hover:bg-[#E60000] hover:text-white px-2 py-1 text-[10px] uppercase tracking-widest font-bold border border-[#E60000] shrink-0">
                    Remove
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div className="space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-gray-400 font-bold mb-1">Add from saved messages</div>
          <select
            value={selectedAdd}
            onChange={(e) => { addQr(e.target.value); }}
            disabled={saving}
            className="border border-gray-300 px-3 py-2 text-sm w-full"
          >
            <option value="">-- Select a Quick Reply or WA Template --</option>
            {quickReplies.length > 0 && (
              <optgroup label="Quick Replies">
                {quickReplies.map((qr) => (
                  <option key={qr.id} value={qr.id}>{qr.title}</option>
                ))}
              </optgroup>
            )}
            {templates.length > 0 && (
              <optgroup label="WhatsApp Templates">
                {templates.map((tpl) => (
                  <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
                ))}
              </optgroup>
            )}
          </select>

          <div className="text-[10px] uppercase tracking-widest text-gray-400 font-bold mb-1 mt-3">Or add a custom message</div>
          <div className="flex gap-2">
            <input
              type="text"
              value={customText}
              onChange={(e) => setCustomText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }}
              placeholder="Type a one-off message (e.g. Thanks for reaching out! 🙏)"
              disabled={saving}
              className="flex-1 border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#002FA7]"
            />
            <button
              onClick={addCustom}
              disabled={saving || !customText.trim()}
              className="bg-[#002FA7] hover:bg-[#002288] text-white px-3 py-2 text-[10px] uppercase tracking-widest font-bold flex items-center gap-1 disabled:opacity-50"
            >
              <Plus size={12} weight="bold" /> Add
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

